import React, { useMemo, useRef, useLayoutEffect, useState } from 'react';
import { LineChart, PieChart } from '@mui/x-charts';
import { ReportData, FileAttachment } from '../home/components/reportComponent/reportTypes';
import { getRiskLevelColor, getRiskLevelText, getCoverPhotoId, getMapImage } from '../home/components/reportComponent/reportUtils';

// ─── Event type colours (same as PinChartModal) ───────────────────────────────
const eventTypeColors: Record<string, string> = {
    'Terörizm (TE)': '#D76E3B',
    'Örgütlü Suçlar (OS)': '#F35E5E',
    'Sosyal ve Politik İstikrarsızlık (SP)': '#67C086',
    'Çevresel ve Doğal Afetler (CA)': '#689D7B',
    'Bilgi Güvenliği ve Enformasyon Tehditleri (BG)': '#63ABC3',
    'Bireysel ve Adi Suçlar (BA)': '#4995DB',
    'Göç ve Mülteci Krizleri (GM)': '#8e6c58',
    'Sağlık ve Biyolojik Tehditler (SB)': '#265454',
    'Yasal ve Düzenleyici Uyumsuzluklar (YD)': '#5C7A9A',
    'Ekonomik ve Finansal Tehditler (EF)': '#9B7A6B',
};

interface HtmlReportTemplateProps {
    reportData: ReportData;
    attachments: { [key: string]: string };
    userData: { firstName: string; lastName: string } | null;
    onPaginationReady?: (isReady: boolean) => void;
    // Chart data – passed from ReportView (read from localStorage)
    eventsData?: any;
    chartStartDate?: Date;
    chartEndDate?: Date;
    selectedEventTypes?: string[];
}

// ─── Layout constants ─────────────────────────────────────────────────────────
const PAD_X = 50;
const PAD_Y = 16;
const HEADER_H = 68;
const FOOTER_H = 40;
let RUNTIME_CONTENT_H = 855;

// Chart render width: page content area minus padding
const CHART_W = 650;

// ─── Chart data processing (mirrors PinChartModal.processEventsData) ──────────
interface WeeklyData { date: Date; count: number; weekLabel: string; }
interface EventTypeData { eventType: string; data: { date: Date; count: number }[]; }
const EMPTY_SELECTED_EVENT_TYPES: string[] = [];

function processChartData(
    eventsData: any,
    startDate: Date,
    endDate: Date,
    selectedEventTypes: string[],
): { weeklyData: WeeklyData[]; eventTypeData: EventTypeData[] } {
    try {
        const rangeStart = new Date(startDate);
        rangeStart.setHours(0, 0, 0, 0);
        const rangeEnd = new Date(endDate);
        rangeEnd.setHours(23, 59, 59, 999);

        const isSameDay = (a: Date, b: Date) =>
            a.getFullYear() === b.getFullYear()
            && a.getMonth() === b.getMonth()
            && a.getDate() === b.getDate();

        const isTodayRange = (() => {
            const today = new Date();
            today.setHours(0, 0, 0, 0);
            return isSameDay(rangeStart, today) && isSameDay(rangeEnd, today);
        })();

        const isLastWeekRange = (() => {
            const now = new Date();
            const today = new Date(now);
            today.setHours(0, 0, 0, 0);
            const dayOfWeek = today.getDay();

            const startOfCurrentWeek = new Date(today);
            startOfCurrentWeek.setDate(today.getDate() - dayOfWeek);

            const endOfCurrentWeek = new Date(startOfCurrentWeek);
            endOfCurrentWeek.setDate(startOfCurrentWeek.getDate() + 6);

            const expectedStart = new Date(startOfCurrentWeek);
            expectedStart.setDate(startOfCurrentWeek.getDate() - 7);
            expectedStart.setHours(0, 0, 0, 0);

            const expectedEnd = new Date(endOfCurrentWeek);
            expectedEnd.setDate(endOfCurrentWeek.getDate() - 7);
            expectedEnd.setHours(0, 0, 0, 0);

            return isSameDay(rangeStart, expectedStart) && isSameDay(rangeEnd, expectedEnd);
        })();

        const allEvents = eventsData.locations
            .filter((loc: any) => loc.district1 !== 'Unknown District')
            .flatMap((loc: any) => loc.events);

        let filtered = allEvents;
        if (selectedEventTypes.length > 0) {
            filtered = allEvents.filter((e: any) => selectedEventTypes.includes(e.eventType));
        }
        filtered = filtered.filter((e: any) => {
            const d = new Date(e.date);
            return d >= rangeStart && d <= rangeEnd;
        });

        const useDailyBuckets = isTodayRange || isLastWeekRange;
        if (useDailyBuckets) {
            const dailyGroups: { [k: string]: number } = {};
            const cursor = new Date(rangeStart);
            while (cursor <= rangeEnd) {
                const key = new Date(cursor.getTime() - cursor.getTimezoneOffset() * 60000).toISOString().split('T')[0];
                dailyGroups[key] = 0;
                cursor.setDate(cursor.getDate() + 1);
            }

            filtered.forEach((e: any) => {
                const d = new Date(e.date);
                d.setHours(0, 0, 0, 0);
                const key = new Date(d.getTime() - d.getTimezoneOffset() * 60000).toISOString().split('T')[0];
                if (dailyGroups[key] !== undefined) {
                    dailyGroups[key]++;
                }
            });

            const dailyArr: WeeklyData[] = Object.entries(dailyGroups)
                .map(([dateKey, count]) => {
                    const day = new Date(dateKey + 'T00:00:00');
                    day.setMinutes(day.getMinutes() + day.getTimezoneOffset());
                    return {
                        date: day,
                        count: count as number,
                        weekLabel: day.toLocaleDateString('tr-TR', { day: '2-digit', month: 'short' }),
                    };
                })
                .sort((a, b) => a.date.getTime() - b.date.getTime());

            const eventTypes = Array.from(new Set(filtered.map((e: any) => e.eventType as string)));
            const eventTypeDataArr: EventTypeData[] = eventTypes.map(et => ({
                eventType: String(et),
                data: dailyArr.map(d => ({
                    date: d.date,
                    count: filtered.filter((e: any) => {
                        const ed = new Date(e.date);
                        const dayStart = new Date(d.date);
                        dayStart.setHours(0, 0, 0, 0);
                        const dayEnd = new Date(dayStart);
                        dayEnd.setDate(dayEnd.getDate() + 1);
                        return e.eventType === et && ed >= dayStart && ed < dayEnd;
                    }).length,
                })),
            }));

            return { weeklyData: dailyArr, eventTypeData: eventTypeDataArr };
        }

        // Weekly groups (Monday-based, same as PinChartModal)
        const firstMonday = new Date(rangeStart);
        firstMonday.setHours(0, 0, 0, 0);
        const day = firstMonday.getDay();
        firstMonday.setDate(firstMonday.getDate() - day + (day === 0 ? -6 : 1));

        const weeklyGroups: { [k: string]: number } = {};
        let cur = new Date(firstMonday);
        while (cur <= rangeEnd) {
            const key = new Date(cur.getTime() - cur.getTimezoneOffset() * 60000).toISOString().split('T')[0];
            weeklyGroups[key] = 0;
            cur.setDate(cur.getDate() + 7);
        }

        filtered.forEach((e: any) => {
            const ed = new Date(e.date);
            ed.setHours(0, 0, 0, 0);
            const edDay = ed.getDay();
            ed.setDate(ed.getDate() - edDay + (edDay === 0 ? -6 : 1));
            const key = new Date(ed.getTime() - ed.getTimezoneOffset() * 60000).toISOString().split('T')[0];
            if (weeklyGroups[key] !== undefined) weeklyGroups[key]++;
        });

        const weeklyArr: WeeklyData[] = Object.entries(weeklyGroups).map(([dateKey, count]) => {
            const monday = new Date(dateKey + 'T00:00:00');
            monday.setMinutes(monday.getMinutes() + monday.getTimezoneOffset());
            const sunday = new Date(monday);
            sunday.setDate(monday.getDate() + 6);
            return {
                date: monday,
                count: count as number,
                weekLabel: `${monday.getDate()} ${monday.toLocaleDateString('tr-TR', { month: 'short' })} - ${sunday.getDate()} ${sunday.toLocaleDateString('tr-TR', { month: 'short' })}`,
            };
        }).sort((a, b) => a.date.getTime() - b.date.getTime());

        // Exclude current (incomplete) week
        const now = new Date(); now.setHours(0, 0, 0, 0);
        const nowDay = now.getDay();
        const curMonday = new Date(now); curMonday.setDate(now.getDate() - nowDay + (nowDay === 0 ? -6 : 1));
        const curMondayTs = curMonday.getTime();

        const last6 = weeklyArr
            .filter(w => { const wm = new Date(w.date); wm.setHours(0, 0, 0, 0); return wm.getTime() !== curMondayTs; })
            .slice(-6);

        // Event type data (for pie chart)
        const eventTypes = Array.from(new Set(filtered.map((e: any) => e.eventType as string)));
        const eventTypeDataArr: EventTypeData[] = eventTypes.map(et => ({
            eventType: String(et),
            data: last6.map(d => ({
                date: d.date,
                count: filtered.filter((e: any) => {
                    const ed = new Date(e.date);
                    return e.eventType === et && ed >= d.date && ed < new Date(d.date.getTime() + 7 * 86400000);
                }).length,
            })),
        }));

        return { weeklyData: last6, eventTypeData: eventTypeDataArr };
    } catch (err) {
        console.warn('Chart data processing failed:', err);
        return { weeklyData: [], eventTypeData: [] };
    }
}

// ─── Block type ────────────────────────────────────────────────────────────────
interface Block { node: React.ReactNode; isHeading: boolean; }

function paginate(blocks: Block[], heights: number[], maxH: number): Block[][] {
    const pages: Block[][] = [];
    let curPage: Block[] = [];
    let curPageH: number[] = [];
    let curH = 0;
    const safeMax = maxH * 0.90;

    for (let i = 0; i < blocks.length; i++) {
        const h = heights[i];
        if (h <= 0) continue;
        if (h > safeMax) {
            if (curPage.length > 0) { pages.push(curPage); curPage = []; curPageH = []; curH = 0; }
            pages.push([blocks[i]]);
            continue;
        }
        if (curH + h > safeMax && curPage.length > 0) {
            const lastBlock = curPage[curPage.length - 1];
            if (lastBlock.isHeading) {
                const orphanH = curPageH.pop()!;
                curPage.pop();
                curH -= orphanH;
                pages.push(curPage);
                curPage = [lastBlock, blocks[i]];
                curPageH = [orphanH, h];
                curH = orphanH + h;
            } else {
                pages.push(curPage);
                curPage = [blocks[i]]; curPageH = [h]; curH = h;
            }
        } else {
            curPage.push(blocks[i]); curPageH.push(h); curH += h;
        }
    }
    if (curPage.length > 0) pages.push(curPage);
    return pages;
}

// ─── Sub-components ───────────────────────────────────────────────────────────
const PageHeader: React.FC = () => (
    <div className="report-page-header">
        <img src="/tav_logo_pdf_header.png" alt="TAV"
            style={{ height: 50, width: 'auto', opacity: 0.7, objectFit: 'contain' }} />
        <img src="/iso_logo_pdf_header.png" alt="ISO"
            style={{ height: 30, width: 'auto', opacity: 0.7, objectFit: 'contain' }} />
    </div>
);

const PageFooter: React.FC<{ pageNum: number; total: number }> = ({ pageNum, total }) => (
    <div className="report-page-footer">
        <span>TAV-GR-2025/07-01 | GF-6.4.3.</span>
        <span>{pageNum} / {total}</span>
    </div>
);

const CoverPage: React.FC<{
    reportData: ReportData;
    attachments: { [key: string]: string };
}> = ({ reportData, attachments }) => {
    const coverPhotoId = getCoverPhotoId(reportData);
    return (
        <div className="report-cover-page" data-export-page="true">
            <img src="/background.jpg" alt="" className="report-cover-bg" />
            <div className="report-cover-inner">
                <div className="report-cover-logo-row">
                    <img src="/tav_logo_white.png" alt="TAV"
                        style={{ width: 110, height: 70, objectFit: 'contain' }} />
                </div>
                <div className="report-cover-body">
                    {coverPhotoId && attachments[coverPhotoId] && (
                        <img src={attachments[coverPhotoId]} alt="Kapak" className="report-cover-photo" />
                    )}
                    <div className="report-cover-title">
                        <span>TESİS RİSK VE TEHDİT</span>
                        <span>DEĞERLENDİRME</span>
                        <span>RAPORU</span>
                    </div>
                    <div className="report-cover-date">
                        {new Date().toLocaleDateString('tr-TR', { day: '2-digit', month: 'long', year: 'numeric' }).toUpperCase()}
                    </div>
                    <div className="report-cover-pin-name">{reportData.pinInfo.Name}</div>
                </div>
                <div className="report-cover-footer">
                    <span>TAV Özel Güvenlik Hizmetleri A.Ş.</span>
                    <span>Strateji Geliştirme ve Kalite Kontrol Koordinatörlüğü</span>
                </div>
            </div>
        </div>
    );
};

// ─── Block helpers ─────────────────────────────────────────────────────────────
let _blockKey = 0;
const bk = () => `b${_blockKey++}`;
function B(el: React.ReactNode, isHeading = false): Block {
    return { node: <div className="rep-block-wrap">{el}</div>, isHeading };
}

// ─── Main template ─────────────────────────────────────────────────────────────
const HtmlReportTemplate: React.FC<HtmlReportTemplateProps> = ({
    reportData, attachments, userData,
    onPaginationReady,
    eventsData, chartStartDate, chartEndDate, selectedEventTypes,
}) => {
    _blockKey = 0;
    const selectedTypes = selectedEventTypes ?? EMPTY_SELECTED_EVENT_TYPES;

    const measureRef = useRef<HTMLDivElement>(null);
    const pageRef = useRef<HTMLDivElement>(null);
    const [pages, setPages] = useState<Block[][]>([]);
    const [ready, setReady] = useState(false);

    // ── Section groupings ────────────────────────────────────────────────────
    const evaluationAnswer = useMemo(() =>
        reportData.questionAnswers.find(qa =>
            qa.answer.questionCategoryName?.toUpperCase() === 'SONUÇ VE DEĞERLENDİRME'
        ), [reportData]);

    const groupedQuestions = useMemo(() =>
        reportData.questionAnswers
            .filter(qa => qa.answer.questionCategoryName?.toUpperCase() !== 'SONUÇ VE DEĞERLENDİRME')
            .reduce((acc, qa) => {
                const cat = qa.answer.questionCategoryName;
                if (cat) { if (!acc[cat]) acc[cat] = []; acc[cat].push(qa.answer); }
                return acc;
            }, {} as Record<string, any[]>),
        [reportData]);

    const groupedRiskAnswers = useMemo(() =>
        (reportData.riskAnswers ?? []).reduce((acc, ra) => {
            if (!acc[ra.categoryName]) acc[ra.categoryName] = [];
            acc[ra.categoryName].push(ra);
            return acc;
        }, {} as Record<string, any[]>),
        [reportData]);

    const mapImageId = useMemo(() => getMapImage(reportData), [reportData]);

    const orderedCategories = useMemo(() => Array.from(
        new Set([...Object.keys(groupedQuestions), ...Object.keys(groupedRiskAnswers)])
    ).filter(cat => {
        const qCount = (groupedQuestions[cat] ?? []).filter(a => a.findings?.trim() || a.proposedSolutions?.trim()).length;
        const rCount = (groupedRiskAnswers[cat] ?? []).length;
        return qCount > 0 || rCount > 0;
    }).sort((a, b) => a.localeCompare(b, 'tr-TR')), [groupedQuestions, groupedRiskAnswers]);

    // ── Chart data (processed here, no capture needed) ───────────────────────
    const { weeklyData, eventTypeData } = useMemo(() => {
        if (!eventsData || !chartStartDate || !chartEndDate) return { weeklyData: [], eventTypeData: [] };
        return processChartData(eventsData, chartStartDate, chartEndDate, selectedTypes);
    }, [eventsData, chartStartDate, chartEndDate, selectedTypes]);

    // ── Build flat block list ────────────────────────────────────────────────
    const blocks = useMemo<Block[]>(() => {
        const result: Block[] = [];

        // ── Rapor Detayları ──
        result.push(B(<h2 key={bk()} className="rep-section-title">RAPOR DETAYLARI</h2>, true));
        result.push(B(<p key={bk()} className="rep-p"><strong>Ait Olduğu Dönem / Tarih:</strong><br />{new Date().toLocaleDateString('tr-TR')}</p>));
        result.push(B(<p key={bk()} className="rep-p"><strong>Düzenleyen Kurum / Kişi:</strong><br />TAV Özel Güvenlik Hizmetleri A.Ş.{userData ? ` / ${userData.firstName} ${userData.lastName}` : ''}</p>));
        result.push(B(<p key={bk()} className="rep-p"><strong>Risk ve Tehdit Analizi Yapılan Kurum:</strong><br />{reportData.pinInfo.Name}</p>));
        result.push(B(<p key={bk()} className="rep-p"><strong>Değerlendirme Yaklaşımı:</strong><br />Risk ve Tehdit Değerlendirme Raporu; Kabiliyet, Maksat, Zafiyet, Kalıntı Risk kavramları dikkate alınarak ve Uzaktan Merkeze Değerlendirme Yöntemi ile hazırlanmıştır.</p>));
        result.push(B(<p key={bk()} className="rep-p"><strong>Raporun Amacı:</strong></p>));
        result.push(B(<p key={bk()} className="rep-bullet">• Risk ve tehdit unsurlarını belirlemek,</p>));
        result.push(B(<p key={bk()} className="rep-bullet">• Bu unsurların değerlendirmesini yaparak risk ve tehdit seviyesini tespit etmek,</p>));
        result.push(B(<p key={bk()} className="rep-bullet">• Tespit edilmiş risk ve tehdit seviyesine istinaden alınması gereken etkin karşı tedbirleri belirlemektir.</p>));

        // ── Genel Bakış ──
        result.push(B(<h2 key={bk()} className="rep-section-title" style={{ marginTop: 24 }}>GENEL BAKIŞ</h2>, true));
        result.push(B(<p key={bk()} className="rep-p">{reportData.pinInfo.reportDescription || 'Genel bakış bilgisi bulunmamaktadır.'}</p>));

        // ── Line Chart (direkt SVG embed) ──
        if (weeklyData.length > 0) {
            result.push(B(<p key={bk()} className="rep-sub-title">Haftalık Olay Sayısı</p>, true));
            result.push(B(
                <div key={bk()} style={{ width: '100%' }}>
                    <LineChart
                        skipAnimation
                        xAxis={[{
                            data: weeklyData.map(d => d.weekLabel),
                            scaleType: 'band',
                            tickLabelStyle: { angle: 30, textAnchor: 'start', fontSize: 10 },
                        }]}
                        yAxis={[{ min: 0 }]}
                        series={[{
                            data: weeklyData.map(d => d.count),
                            curve: 'linear',
                            showMark: true,
                            color: '#094151',
                            valueFormatter: (v) => v ? `Olay Sayısı: ${v}` : null,
                        }]}
                        width={CHART_W}
                        height={240}
                        margin={{ left: 40, right: 20, top: 16, bottom: 70 }}
                        sx={{
                            '.MuiLineElement-root': { strokeWidth: 2 },
                            '.MuiMarkElement-root': { strokeWidth: 2, r: 4, stroke: '#fff', fill: 'currentColor' },
                            /* Make static and prevent tooltips/hover scaling that break PDF generation */
                            pointerEvents: 'none',
                        }}
                        tooltip={{ trigger: 'none' }} // Disable interactive tooltip
                        disableAxisListener // Disable axis hover interactions
                        slotProps={{ legend: { hidden: true } }}
                    />
                </div>
            ));
        }

        // ── Pie Chart (direkt SVG embed) ──
        if (eventTypeData.length > 0) {
            const pieData = eventTypeData
                .map((td, idx) => {
                    const count = td.data.reduce((s, d) => s + d.count, 0);
                    return {
                        id: td.eventType,
                        value: count,
                        label: `${td.eventType} (${count})`,
                        color: eventTypeColors[td.eventType] || `hsl(${idx * (360 / Math.max(eventTypeData.length, 1))}, 70%, 50%)`,
                    };
                })
                .filter(item => item.value > 0);

            if (pieData.length > 0) {
            result.push(B(<p key={bk()} className="rep-sub-title">Olay Tiplerine Göre Dağılım</p>, true));
            result.push(B(
                <div key={bk()} style={{ width: '100%' }}>
                    <PieChart
                        skipAnimation
                        series={[{
                            data: pieData,
                            highlightScope: { faded: 'none', highlighted: 'none' }, // Remove hover scaling completely
                            arcLabel: (item) => `${item.value}`,
                        }]}
                        width={CHART_W}
                        height={240}
                        margin={{ right: 200 }}
                        sx={{ pointerEvents: 'none' }} // Ensure no mouse hits are accepted to show popups
                        tooltip={{ trigger: 'none' }} // Ensure tooltips are bypassed
                        slotProps={{
                            legend: {
                                direction: 'column',
                                position: { vertical: 'middle', horizontal: 'right' },
                                padding: { left: 0, right: 10, top: 0, bottom: 0 },
                                itemMarkWidth: 8, itemMarkHeight: 8,
                                markGap: 4, itemGap: 6,
                                labelStyle: { fontSize: 10 },
                            },
                        }}
                    />
                </div>
            ));
            }
        }

        // ── Harita ──
        if (mapImageId && attachments[mapImageId]) {
            result.push(B(<p key={bk()} className="rep-sub-title">Fonksiyonel Konum ve Erişim Ağı</p>, true));
            result.push(B(<img key={bk()} src={attachments[mapImageId]} alt="Harita" className="rep-img" style={{ maxHeight: 260 }} />));
            result.push(B(<p key={bk()} className="rep-p" style={{ textAlign: 'center', fontSize: 11, color: '#666' }}>Seçilen yarıçap: {reportData.pinInfo.RadiusInMeters} m.</p>));
        }

        // ── Risk Analizi Metodolojisi ──
        result.push(B(<h2 key={bk()} className="rep-section-title" style={{ marginTop: 24 }}>RİSK ANALİZİ METODOLOJİSİ</h2>, true));
        result.push(B(<p key={bk()} className="rep-p">Bu rapor, güvenlik risklerini oluşturan ana bileşenler arasındaki nedensel ilişki zincirini aşağıdaki formüller ile modellemektedir.</p>));
        result.push(B(
            <table key={bk()} className="rep-table">
                <thead><tr><th>Formül</th><th>Açıklama</th><th>İlgili Fonksiyon</th></tr></thead>
                <tbody>
                    <tr><td>Kabiliyet + Maksat → Tehdit</td><td>Aktörün eylem kapasitesi ile niyetinin birleşimi</td><td>İstihbarat</td></tr>
                    <tr><td>Tehdit + Zafiyet → Risk</td><td>Tehditlerin mevcut güvenlik açıklarıyla etkileşimi</td><td>Operasyon &amp; Araştırma</td></tr>
                    <tr><td>Risk – Tedbirler → Kalıntı Risk</td><td>Alınan önlemler sonrası geriye kalan risk seviyesi</td><td>Önleyici Güvenlik Planlaması</td></tr>
                </tbody>
            </table>
        ));
        [
            ['Maksat', 'Eylem gerçekleştirme niyeti.'],
            ['Kabiliyet', 'Bu niyetin fiiliyata dönüştürülebilmesini kolaylaştıran imkân ve kapasite.'],
            ['Tehdit', 'Maksat ve kabiliyetin birleşiminden doğan potansiyel tehlike.'],
            ['Risk', 'Bu tehdidin, mevcut zafiyetlerle etkileşime girmesi sonucu oluşan zarar ihtimali.'],
        ].forEach(([term, desc]) => {
            result.push(B(<p key={bk()} className="rep-p"><strong style={{ color: '#003087' }}>{term}:</strong> {desc}</p>));
        });

        result.push(B(<h2 key={bk()} className="rep-section-title" style={{ marginTop: 24 }}>RİSK DEĞERLENDİRME SKALASI</h2>, true));
        result.push(B(<p key={bk()} className="rep-p">Risk seviyesi, nitelikli saha gözlemleri, yapay zekâ destekli veri analizi ve uzman değerlendirmeleri doğrultusunda sınıflandırılmıştır.</p>));
        result.push(B(
            <table key={bk()} className="rep-table" style={{ width: '60%' }}>
                <thead><tr><th>Kalıntı Risk (r)</th><th>Alarm Seviyesi</th></tr></thead>
                <tbody>
                    <tr><td>r &lt; 3</td><td style={{ backgroundColor: '#e0e0e0', fontWeight: 600 }}>DÜŞÜK</td></tr>
                    <tr><td>3 ≤ r ≤ 10</td><td style={{ backgroundColor: '#fff176', fontWeight: 600 }}>ORTA</td></tr>
                    <tr><td>10 &lt; r &lt; 20</td><td style={{ backgroundColor: '#ffb74d', fontWeight: 600 }}>YÜKSEK</td></tr>
                    <tr><td>r ≥ 20</td><td style={{ backgroundColor: '#ef5350', color: 'white', fontWeight: 600 }}>KRİTİK</td></tr>
                </tbody>
            </table>
        ));

        // ── Category sections ──
        orderedCategories.forEach(category => {
            const qAnswers = (groupedQuestions[category] ?? []).filter(a => a.findings?.trim() || a.proposedSolutions?.trim());
            const rAnswers = groupedRiskAnswers[category] ?? [];

            result.push(B(<h2 key={bk()} className="rep-section-title" style={{ marginTop: 24 }}>{category.toLocaleUpperCase('tr-TR')}</h2>, true));

            qAnswers.forEach((ans: any, idx: number) => {
                if (ans.findings?.trim()) result.push(B(<p key={bk()} className="rep-p"><strong>{idx + 1}. Bulgu: </strong>{ans.findings}</p>));
                if (ans.proposedSolutions?.trim()) result.push(B(<p key={bk()} className="rep-p"><strong>{idx + 1}. Öneri: </strong>{ans.proposedSolutions}</p>));
                (ans.fileAttachments ?? []).filter((f: FileAttachment) => attachments[f.id]).forEach((f: FileAttachment) => {
                    result.push(B(<img key={bk()} src={attachments[f.id]} alt="Ek görsel" className="rep-img" />));
                });
            });

            if (rAnswers.length > 0) {
                result.push(B(<p key={bk()} className="rep-sub-title">RİSK TABLOSU</p>, true));
                (['Öncesi', 'Sonrası'] as const).forEach(k => {
                    const isAfter = k === 'Sonrası';
                    const row = rAnswers.find((r: any) => r.isAfterMeasures === isAfter);
                    if (!row) return;
                    const color = getRiskLevelColor(row.residualRiskLevel);
                    result.push(B(
                        <div key={bk()}>
                            <p className="rep-p" style={{ fontWeight: 600, marginBottom: 4 }}>Etkin Karşı Tedbirler {k} Risk ve Tehdit Seviyesi</p>
                            <table className="rep-risk-table">
                                <thead>
                                    <tr><th>Olasılık</th><th>Etki</th><th>Tehdit</th><th>Tedbirler Kat Sayısı</th><th>Kalıntı Risk</th><th>Risk Seviyesi</th></tr>
                                </thead>
                                <tbody>
                                    <tr>
                                        <td>{row.probabilityLevel}</td>
                                        <td>{row.impactLevel}</td>
                                        <td>{row.threatLevel}</td>
                                        <td>{row.appliedMeasureCoefficient}</td>
                                        <td>{row.residualRiskLevel}</td>
                                        <td style={{ backgroundColor: color.backgroundColor, color: color.color, fontWeight: 700 }}>{getRiskLevelText(row.residualRiskLevel)}</td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>
                    ));
                });
            }
        });

        // ── Sonuç ve Değerlendirme ──
        if (evaluationAnswer) {
            result.push(B(<h2 key={bk()} className="rep-section-title" style={{ marginTop: 24 }}>SONUÇ VE DEĞERLENDİRME</h2>, true));
            const descText = evaluationAnswer.answer.description ?? '';
            descText.split(/\n+/).filter(Boolean).forEach(para => {
                result.push(B(<p key={bk()} className="rep-p">{para}</p>));
            });
            (evaluationAnswer.answer.fileAttachments ?? [])
                .filter((f: FileAttachment) => attachments[f.id])
                .forEach((f: FileAttachment) => {
                    result.push(B(<img key={bk()} src={attachments[f.id]} alt="Değerlendirme ek" className="rep-img" />));
                });
        }

        return result;
    }, [reportData, attachments, userData, orderedCategories, evaluationAnswer,
        groupedQuestions, groupedRiskAnswers, mapImageId, weeklyData, eventTypeData]);

    // ── Two-pass pagination ────────────────────────────────────────────────────
    useLayoutEffect(() => {
        if (!measureRef.current || blocks.length === 0) return;
        setReady(false);
        onPaginationReady?.(false);
        const container = measureRef.current;
        let cancelled = false;
        let finished = false;
        let retryTimer: number | null = null;
        let fallbackTimer: number | null = null;
        let hardTimeout: number | null = null;
        let onLoad: (() => void) | null = null;
        const MAX_ATTEMPTS = 120; // ~6s with 50ms retry interval
        const RETRY_MS = 50;

        const finishWithFallback = () => {
            if (cancelled || finished) return;
            finished = true;
            if (hardTimeout !== null) {
                window.clearTimeout(hardTimeout);
                hardTimeout = null;
            }
            setPages([blocks]);
            setReady(true);
            onPaginationReady?.(true);
        };

        const run = (attempt = 0) => {
            if (cancelled || finished) return;
            const children = Array.from(container.children) as HTMLElement[];
            if (children.length !== blocks.length) {
                if (attempt < MAX_ATTEMPTS) {
                    retryTimer = window.setTimeout(() => run(attempt + 1), RETRY_MS);
                    return;
                }
                // Avoid permanent loading state if hidden measurement children never settle.
                finishWithFallback();
                return;
            }

            const liveContent = pageRef.current?.querySelector('.report-page-content') as HTMLElement | null
                ?? document.querySelector('.report-page-content') as HTMLElement | null;
            if (liveContent) RUNTIME_CONTENT_H = liveContent.clientHeight - 4;

            const heights = children.map(el => el.getBoundingClientRect().height);
            const paginated = paginate(blocks, heights, RUNTIME_CONTENT_H);
            finished = true;
            if (hardTimeout !== null) {
                window.clearTimeout(hardTimeout);
                hardTimeout = null;
            }
            setPages(paginated);
            setReady(true);
            onPaginationReady?.(true);
        };

        const imgs = Array.from(container.querySelectorAll('img'));
        const pending = imgs.filter(img => !img.complete || img.naturalHeight === 0);

        if (pending.length === 0) {
            retryTimer = window.setTimeout(() => run(), 0);
        } else {
            let loaded = 0;
            onLoad = () => {
                if (++loaded === pending.length) {
                    retryTimer = window.setTimeout(() => run(), 0);
                }
            };
            pending.forEach(img => {
                img.addEventListener('load', onLoad);
                img.addEventListener('error', onLoad);
            });
            fallbackTimer = window.setTimeout(() => {
                run(MAX_ATTEMPTS);
            }, 5000);
        }

        // Hard safety-net: never keep spinner forever.
        hardTimeout = window.setTimeout(() => {
            finishWithFallback();
        }, 8000);

        return () => {
            cancelled = true;
            if (retryTimer !== null) window.clearTimeout(retryTimer);
            if (fallbackTimer !== null) window.clearTimeout(fallbackTimer);
            if (hardTimeout !== null) window.clearTimeout(hardTimeout);
            pending.forEach(img => {
                if (!onLoad) return;
                img.removeEventListener('load', onLoad);
                img.removeEventListener('error', onLoad);
            });
        };
    }, [blocks, onPaginationReady]);

    useLayoutEffect(() => {
        return () => onPaginationReady?.(false);
    }, [onPaginationReady]);

    return (
        <div className="report-outer">
            {/* Cover */}
            <CoverPage reportData={reportData} attachments={attachments} />

            {/* Hidden measurement container – same CSS class as actual page content for identical font/width context */}
            <div
                ref={measureRef}
                aria-hidden="true"
                className="report-page-content"
                style={{
                    position: 'fixed',
                    top: 0,
                    left: '-9999px',
                    width: '210mm',
                    height: 'auto',
                    maxHeight: 'none',
                    overflow: 'visible',
                    visibility: 'hidden',
                    pointerEvents: 'none',
                    zIndex: -1,
                }}
            >
                {blocks.map((block, i) => (
                    <div key={i}>{block.node}</div>
                ))}
            </div>

            {/* Paginated content */}
            {ready ? (
                pages.map((pageBlocks, i) => (
                    <div key={i} ref={i === 0 ? pageRef : undefined} className="report-a4-page" data-export-page="true">
                        <PageHeader />
                        <div className="report-page-content">
                            {pageBlocks.map((block, j) => (
                                <React.Fragment key={j}>{block.node}</React.Fragment>
                            ))}
                        </div>
                        <PageFooter pageNum={i + 1} total={pages.length} />
                    </div>
                ))
            ) : (
                <div ref={pageRef} className="report-a4-page">
                    <PageHeader />
                    <div className="report-page-content" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column', gap: 16 }}>
                        <div style={{ width: 40, height: 40, border: '4px solid #E2E8F0', borderTopColor: '#003087', borderRadius: '50%', animation: 'spin 0.8s linear infinite' }} />
                        <p style={{ color: '#64748B', fontSize: 14, margin: 0 }}>Sayfa düzeni hesaplanıyor…</p>
                        <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
                    </div>
                    <PageFooter pageNum={1} total={1} />
                </div>
            )}
        </div>
    );
};

export default HtmlReportTemplate;
